import React, { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  TextField,
  Typography,
  Alert,
  Chip,
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { toast } from 'react-toastify';
import { CartContext } from '../context/CartContext';
import ProductCard from './ProductCard';
import Faq from './Faq';

const API_URL = 'https://joli-mot.fr/api-2026.php';
const CLE_API = '34KVHFQSP226YTk4n256QP6rGbqhA5w2ymY7W3pLwdbuc3i75';

const CATEGORY_LABELS = [
  'Mariage', 'Naissance', 'Grossesse', 'Baptême', 'Marraine',
  'Témoin', 'Demande originale', 'Anniversaire', 'Fête', 'Surprise',
];

const SEO_INTRO = `Découvrez notre collection complète de cartes à gratter numériques personnalisées.
Chaque visuel est disponible pour toutes les occasions : mariage, naissance, grossesse,
baptême, demande de marraine, demande de témoin, anniversaire...
Choisissez le visuel qui vous correspond, ajoutez votre message secret,
et envoyez une surprise unique et émouvante à vos proches.`;

// Visuels "fête"/saisonniers (Noël, Pâques…) : toujours affichés en dernier, quelle que
// soit leur performance sur Etsy — ce ne sont pas des visuels "toute l'année" et ils ne
// doivent pas dominer le haut de la page. Détection par préfixe de fichier.
const FETE_PREFIX_REGEX = /^(noel|paques|halloween|saint-valentin)-/i;

// Classement par performance Etsy (nombre de commandes, toutes fiches confondues,
// statistiques "depuis l'ouverture" consultées le 2026-09-16). Les visuels non listés ici
// gardent leur ordre d'origine (le plus récemment ajouté à la bibliothèque en premier).
// ⚠️ Approximatif : Etsy ne donne pas le SKU dans ses statistiques, la correspondance
// titre de fiche → visuel a été faite à la main à partir de la page "Fiches produits" ;
// certains titres sont réutilisés par plusieurs fiches (renouvellements) avec des visuels
// différents, en particulier autour des rangs 2, 5, 6, 9 et 10 — à corriger si un visuel
// est mal placé une fois que le mapping exact SKU↔commande sera disponible (cf. roadmap).
const ETSY_SALES_RANK = [
  'fleurs-aquarelles-bleues.jpg', // #1 très loin devant : 551 commandes / 1356 € (all-time)
  'billet-surprise-fleuri.jpg',   // ~129 + 47 commandes cumulées sur 2 fiches
  'gender-taches.jpg',            // ~68 + 33 commandes cumulées sur 2 fiches
  'mariage-aqua-rose-orange.jpg', // 83 commandes
  'surprise-is-love.jpg',         // ~50 + 25 commandes cumulées
  'feuilles-or-1.jpg',            // 167 commandes (titre de fiche dupliqué avec tulipes.jpg)
  'tulipes.jpg',
  'mariage-pampa-terracotta.jpg', // 13 commandes
  'nouvel-event-fleur.jpeg',      // 6 commandes
  'Roses-origami.png',            // 2 commandes
];

// Tri stable : fête toujours après le reste ; dans chaque groupe, meilleurs vendeurs
// Etsy d'abord, puis tout le reste dans son ordre d'origine (API).
function sortVisualsForDisplay(list) {
  return [...list].sort((a, b) => {
    const aFete = FETE_PREFIX_REGEX.test(a.visuel) ? 1 : 0;
    const bFete = FETE_PREFIX_REGEX.test(b.visuel) ? 1 : 0;
    if (aFete !== bFete) return aFete - bFete;

    const aRank = ETSY_SALES_RANK.indexOf(a.visuel);
    const bRank = ETSY_SALES_RANK.indexOf(b.visuel);
    const aScore = aRank === -1 ? ETSY_SALES_RANK.length : aRank;
    const bScore = bRank === -1 ? ETSY_SALES_RANK.length : bRank;
    return aScore - bScore;
  });
}

// Titre lisible dérivé du nom de fichier, en attendant une colonne `titre` en base (A2.5).
// "noel-rouge-enfantin.jpg" → "Noël rouge enfantin"
const WORD_FIXES = { noel: 'Noël', paques: 'Pâques', bebe: 'Bébé', feuillor: "Feuilles d'or", aqua: 'aquarelle' };
function prettyTitle(visuel) {
  const words = String(visuel || '').replace(/\.[^.]+$/, '').split(/[-_]+/).filter(Boolean);
  const fixed = words.map((w) => WORD_FIXES[w.toLowerCase()] || w.toLowerCase());
  const s = fixed.join(' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Filtre par thème, uniquement là où le nom de fichier permet une déduction fiable.
// Les autres visuels (floraux génériques) conviennent à toutes les occasions et restent
// visibles quel que soit le filtre. Une vraie colonne `theme` en base remplacera ceci (A2.5).
// Règles de thème sur le nom de fichier du fond. Une puce n'est affichée que si au moins
// un visuel en base y correspond (voir availableThemes) : jamais de filtre vide.
const THEME_RULES = {
  'Mariage': /mariage|wedding|love|tulipes/i,
  'Témoin': /love|feuill/i,
  'Naissance': /gender|bebe|ourson|naissance|nouvel-event/i,
  'Grossesse': /bebe|ourson|nouvel-event/i,
  'Surprise': /surprise|billet/i,
  'Fête': /^(noel|paques|halloween|saint-valentin)-/i,
};
function matchesTheme(visuel, theme) {
  const rule = THEME_RULES[theme];
  if (!rule) return true;
  return rule.test(visuel);
}

export default function VisualCatalogPage() {
  const { addToCart } = useContext(CartContext);
  const [visuals, setVisuals] = useState([]);
  const [avgPriceCents, setAvgPriceCents] = useState(250);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [messages, setMessages] = useState({});
  const [addedIds, setAddedIds] = useState(new Set());
  const [activeTheme, setActiveTheme] = useState(null);
  // `?theme=mariage` (liens des menus par événement de l'en-tête) présélectionne le filtre.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const sansAccent = (t) => String(t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const demande = sansAccent(searchParams.get('theme'));
    if (demande) setActiveTheme(CATEGORY_LABELS.find((label) => sansAccent(label) === demande) || null);
  }, [searchParams]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [visualsRes, productsRes] = await Promise.all([
          axios.get(API_URL, { params: { cleApi: CLE_API, service: 'adminListVisuals' } }),
          axios.get(API_URL, { params: { cleApi: CLE_API, service: 'getProducts' } }),
        ]);

        const visualsList = visualsRes.data?.data || [];
        setVisuals(sortVisualsForDisplay(visualsList));

        const products = Array.isArray(productsRes.data)
          ? productsRes.data
          : productsRes.data?.data || [];
        // Prix d'une carte = moyenne des produits CARTES À GRATTER uniquement. Sans ce filtre, les
        // faire-part animés (6,90 à 19,90 €) et la galerie (59,90 €) faussent le prix des cartes.
        const prices = products
          .filter((p) => !p.option_schema && !/^(ms-|galerie)/.test(String(p.sku || '')))
          .map((p) => parseFloat(p.prix_ttc))
          .filter((v) => !isNaN(v) && v > 0);
        if (prices.length > 0) {
          const avg = prices.reduce((s, v) => s + v, 0) / prices.length;
          setAvgPriceCents(Math.round(avg * 100));
        }
        setError('');
      } catch (err) {
        setError('Impossible de charger les visuels. Veuillez réessayer.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleMessageChange = (id, value) => {
    setMessages((prev) => ({ ...prev, [id]: value }));
  };

  const handleAddToCart = (visual) => {
    const msg = (messages[visual.id] || '').trim();
    if (!msg) {
      toast.warning('Veuillez écrire un message personnalisé avant d\'ajouter au panier.', {
        position: 'top-right', autoClose: 3000,
      });
      return;
    }

    addToCart({
      id: `visual-${visual.id}`,
      name: `Carte à gratter – ${visual.visuel.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' ')}`,
      price: avgPriceCents,
      basePrice: avgPriceCents,
      extraScratches: 0,
      maxScratchAllowed: 2,
      customText: msg,
      selectedVisual: `/assets_react/visuels/${visual.visuel}`,
      visuelSku: visual.visuel,
      featuredImage: `/assets_react/visuels/${visual.visuel}`,
    });

    setAddedIds((prev) => new Set(prev).add(visual.id));
    setTimeout(() => {
      setAddedIds((prev) => { const s = new Set(prev); s.delete(visual.id); return s; });
    }, 2000);

    toast.success('Carte ajoutée au panier !', { position: 'top-right', autoClose: 2500 });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const priceEur = (avgPriceCents / 100).toFixed(2);
  // Un thème sans aucun visuel (lien de menu vers un thème pas encore illustré) retombe sur tout le catalogue.
  const themed = activeTheme ? visuals.filter((v) => matchesTheme(v.visuel, activeTheme)) : visuals;
  const visibleVisuals = themed.length > 0 ? themed : visuals;
  // Thèmes réellement représentés dans la base, dans l'ordre de CATEGORY_LABELS
  const availableThemes = CATEGORY_LABELS.filter(
    (label) => THEME_RULES[label] && visuals.some((v) => matchesTheme(v.visuel, label))
  );

  return (
    <Box sx={{ bgcolor: '#fdf8f5', minHeight: '100vh', pb: 8 }}>

      <Helmet>
        <title>Cartes à gratter numériques personnalisées | Joli Ptit Mot</title>
        <meta
          name="description"
          content="Cartes à gratter numériques personnalisées : mariage, grossesse, naissance, baptême, demande de témoin ou de marraine. Message secret, envoi immédiat."
        />
        <link rel="canonical" href="https://joli-mot.fr/cartes-a-gratter-personnalisees" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Cartes à gratter numériques personnalisées | Joli Ptit Mot" />
        <meta property="og:description" content="Choisissez votre visuel, écrivez votre message secret et envoyez une carte à gratter numérique unique." />
        <meta property="og:url" content="https://joli-mot.fr/cartes-a-gratter-personnalisees" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://joli-mot.fr/assets_react/visuels/fleurs-aquarelles-bleues.jpg" />
      </Helmet>

      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
      >
        <Box
          sx={{
            background: 'linear-gradient(135deg, #fdf0ee 0%, #ece8f7 52%, #fdf4ec 100%)',
            py: { xs: 5, md: 8 },
            px: 2,
            textAlign: 'center',
            borderBottom: '1px solid rgba(232,213,209,0.5)',
          }}
        >
          <Typography
            variant="h1"
            component="h1"
            gutterBottom
            sx={{ fontSize: { xs: '2rem', md: '2.8rem' } }}
          >
            Cartes à gratter personnalisées
          </Typography>
          <Typography
            component="p"
            sx={{
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontStyle: 'italic',
              fontSize: { xs: '1.05rem', md: '1.2rem' },
              color: '#7a6060',
              maxWidth: 620,
              mx: 'auto',
              mb: 3,
              lineHeight: 1.7,
            }}
          >
            {SEO_INTRO}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', mb: 2.5 }}>
            {/* Seuls les thèmes qui ont au moins un visuel sont proposés (availableThemes). */}
            {['Tous', ...availableThemes].map((label) => {
              const filterable = true;
              const active = label === 'Tous' ? activeTheme === null : activeTheme === label;
              return (
                <Chip
                  key={label}
                  label={label}
                  size="small"
                  clickable={filterable}
                  onClick={filterable ? () => setActiveTheme(label === 'Tous' ? null : label) : undefined}
                  sx={{
                    bgcolor: active ? '#8C8BC4' : 'rgba(255,255,255,0.75)',
                    color: active ? '#fff' : '#3a2b2b',
                    fontWeight: 500,
                    border: '1px solid rgba(232,213,209,0.5)',
                    backdropFilter: 'blur(6px)',
                    opacity: filterable ? 1 : 0.7,
                  }}
                />
              );
            })}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
            Prix unitaire : <strong>{priceEur.replace('.', ',')} € TTC</strong> — livraison instantanée par email
          </Typography>
        </Box>
      </motion.div>

      <Container maxWidth="xl" sx={{ mt: 5 }}>
        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress sx={{ color: '#c48f8b' }} />
          </Box>
        ) : (
          <Grid container spacing={2.5}>
            {visibleVisuals.map((visual, idx) => {
              const isAdded = addedIds.has(visual.id);
              const label = prettyTitle(visual.visuel);

              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={visual.id} sx={{ display: 'flex' }}>
                  <motion.div
                    initial={{ opacity: 0, y: 28 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-60px' }}
                    transition={{
                      duration: 0.45,
                      ease: [0.16, 1, 0.3, 1],
                      delay: Math.min(idx % 4 * 0.07, 0.28),
                    }}
                    style={{ width: '100%' }}
                  >
                    <ProductCard
                      visuels={[visual]}
                      title={label}
                      price={priceEur}
                    >
                      <TextField
                        size="small"
                        multiline
                        minRows={2}
                        maxRows={4}
                        placeholder="Votre message secret personnalisé…"
                        value={messages[visual.id] || ''}
                        onChange={(e) => handleMessageChange(visual.id, e.target.value)}
                        inputProps={{ maxLength: 200 }}
                        sx={{ mt: 'auto', width: '100%' }}
                      />
                      <Button
                        variant="contained"
                        color={isAdded ? 'success' : 'secondary'}
                        fullWidth
                        onClick={() => handleAddToCart(visual)}
                        startIcon={isAdded ? <CheckCircleIcon /> : <ShoppingCartIcon />}
                        disabled={isAdded}
                        sx={{ mt: 0.5, borderRadius: '50px' }}
                      >
                        {isAdded ? 'Ajouté !' : 'Ajouter au panier'}
                      </Button>
                    </ProductCard>
                  </motion.div>
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* SEO Content */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        >
        <Box
          sx={{
            mt: 8,
            p: { xs: 3, md: 5 },
            bgcolor: '#fefcfa',
            borderRadius: '24px',
            border: '1px solid rgba(232,213,209,0.45)',
            boxShadow: '0 4px 24px rgba(196,143,139,0.08)',
          }}
        >
          <Typography variant="h2" component="h2" gutterBottom sx={{ mb: 3 }}>
            La carte à gratter numérique : une surprise inoubliable pour chaque occasion
          </Typography>

          <Typography variant="body1" paragraph>
            La <strong>carte à gratter numérique</strong> est devenue l'une des façons les plus originales et
            touchantes d'annoncer une grande nouvelle ou de formuler une demande spéciale. Contrairement à une
            carte classique, elle invite la personne qui la reçoit à participer activement : elle gratte la zone
            mystère et découvre un message caché, personnalisé rien que pour elle.
          </Typography>

          <Typography variant="h5" component="h3" gutterBottom fontWeight={600} sx={{ mt: 4 }}>
            Pour quel type d'annonce ?
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { title: 'Mariage & demande de témoins', text: 'Demandez à vos proches de jouer un rôle clé lors de votre grand jour. La surprise du grattage rend ce moment encore plus mémorable et émotionnel.' },
              { title: 'Annonce de grossesse', text: 'Révélez votre grossesse à votre famille et à vos amis de manière unique. La carte à gratter grossesse transforme l\'annonce en un vrai moment de bonheur partagé.' },
              { title: 'Demande de marraine ou parrain', text: 'Votre proche découvre sous la zone à gratter son nouveau rôle dans la vie de votre enfant — un souvenir impérissable.' },
              { title: 'Baptême & naissance', text: 'Annoncez l\'arrivée d\'un bébé ou invitez vos proches à un baptême avec une carte personnalisée, chaleureuse et unique.' },
            ].map(({ title, text }) => (
              <Grid item xs={12} sm={6} key={title}>
                <Box sx={{ p: 2.5, bgcolor: '#f8f4ff', borderRadius: '16px', height: '100%', border: '1px solid rgba(232,213,209,0.4)' }}>
                  <Typography
                    variant="h6"
                    component="h4"
                    gutterBottom
                    sx={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, fontSize: '1.15rem !important' }}
                  >
                    {title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>{text}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Typography variant="h5" component="h3" gutterBottom fontWeight={600} sx={{ mt: 4 }}>
            Pourquoi choisir une carte à gratter numérique ?
          </Typography>
          <Typography variant="body1" paragraph>
            Livrée instantanément par email ou par lien. Pas de délai, pas de risque de perte.
            Chaque carte est entièrement <strong>personnalisable</strong> : choisissez le visuel, rédigez votre
            message secret, et partagez le lien avec vos proches. Ils peuvent gratter depuis leur téléphone,
            tablette ou ordinateur, à tout moment.
          </Typography>

          <Typography variant="h5" component="h3" gutterBottom fontWeight={600} sx={{ mt: 4 }}>
            Comment ça marche ?
          </Typography>
          <Box component="ol" sx={{ pl: 3 }}>
            {[
              'Choisissez un visuel parmi notre collection (fleurs, aquarelle, or, pastels…)',
              'Rédigez votre message secret dans le champ prévu',
              'Ajoutez la carte à votre panier et finalisez la commande',
              'Recevez instantanément un lien vers votre carte à gratter personnalisée',
              'Partagez le lien ou affichez la carte en direct pour la grande révélation !',
            ].map((step, i) => (
              <Box component="li" key={i} sx={{ mb: 1 }}>
                <Typography variant="body1">{step}</Typography>
              </Box>
            ))}
          </Box>

          <Typography variant="body1" sx={{ mt: 3 }}>
            Toutes nos cartes sont disponibles au même tarif :{' '}
            <strong>{priceEur.replace('.', ',')} € TTC</strong>, livraison instantanée incluse.
          </Typography>
        </Box>
        </motion.div>
        <Faq page="cartes" />
      </Container>
    </Box>
  );
}
